import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import {
    buildCreateProfileWizardSearch,
    buildCreateProfileWizardLocationState,
    CreateProfileReturnMode,
} from '../Competencies/createProfileWizardNavigation';
import { CompetencyProfileCard } from './components/CompetencyProfile/CompetencyProfileCard';
import { GrowthForbiddenNotice, GrowthTab } from './components/GrowthTab';
import { NotesTab } from './components/NotesTab';
import { ObjectivesTab } from './components/ObjectivesTab';
import { PlaceholderTab } from './components/PlaceholderTab/PlaceholderTab';
import { ProfileAssignmentScreen } from './components/ProfileAssignmentScreen/ProfileAssignmentScreen';
import { ProfileTabs, type TabValue } from './components/ProfileTabs/ProfileTabs';
import { AssessmentForbiddenNotice } from './components/Summary/AssessmentForbiddenNotice';
import { AssessmentTab } from './components/Summary/SummaryTab';
import { useProfileScore } from './hooks/useProfileScore';
import { useRosterEmployee } from './hooks/useRosterEmployee';

import type { AssignmentScreen } from './components/ProfileAssignmentScreen/ProfileAssignmentScreen';

import { isRefusedError } from '@/api/api';
import { useGetUserById, useGetUserPermissions } from '@/api/directory';
import { useGetEmployeeCompetencyAssignment } from '@/api/performance-profile';
import { userInstanceAtom } from '@/atoms/userDetails';
import { useEmbeddedMode } from '@/contexts/EmbeddedModeContext';
import { useNavigate } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { scrollPageToTop } from '@/navigation/ScrollToTop';
import { RouteName } from '@/navigation/types';
import { getProfilePictureUrl } from '@/utils/user';

type ViewMode = 'default' | AssignmentScreen;

interface PerformanceProfilePageProps {
    employeeId?: string;
    /** The reader is the employee, without the performance profile: only their objectives -- and,
     * when they manage their own objectives, their competencies -- are shown, and nothing the rest
     * of the profile would ask the server for. */
    ownView?: OwnProfileView;
}

export type OwnProfileView = 'objectives' | 'objectivesAndCompetencies';

const OWN_OBJECTIVES_TABS: readonly [TabValue, ...TabValue[]] = ['objectives'];
const OWN_OBJECTIVES_AND_COMPETENCIES_TABS: readonly [TabValue, ...TabValue[]] = [
    'objectives',
    'competencies',
];

/** A line standing in for the whole profile while it cannot be shown. */
const ProfileStatus = ({ text, isError = false }: { text: string; isError?: boolean }) => (
    <div className={'flex items-center justify-center p-8'}>
        <div className={isError ? 'text-error' : 'text-obj-gray'}>{text}</div>
    </div>
);

export const PerformanceProfilePage = ({
    employeeId: propEmployeeId,
    ownView,
}: PerformanceProfilePageProps) => {
    const ownObjectivesOnly = ownView !== undefined;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const history = useHistory();
    // This page is only ever mounted from its own entry point, which puts it under a Router either
    // way -- the module's own when embedded, the app's otherwise. The prop still wins: the host
    // names the employee it opened the profile on, and the address may name another.
    const { id: routeId } = useParams<{ id?: string }>();
    const id = propEmployeeId || routeId;
    const embedded = useEmbeddedMode();
    const [viewMode, setViewMode] = useState<ViewMode>('default');
    // An assignment screen stands in for the tabs; closing it reopens the tab it was opened from,
    // not the row's first one. Both ways, the new screen opens at the top of the page.
    const [openingTab, setOpeningTab] = useState<TabValue>();
    const openAssignment = (from: TabValue, screen: AssignmentScreen = 'assign') => {
        setOpeningTab(from);
        setViewMode(screen);
        scrollPageToTop();
    };
    const closeAssignment = () => {
        setViewMode('default');
        scrollPageToTop();
    };
    // The period lens belongs to the page: the objectives tab and the summary's objectives card
    // read the same one, so the profile never shows two period pickers that disagree. `null`
    // means "whatever `usePeriodOptions` calls the default" -- the page does not duplicate that
    // rule, it just remembers a reader's choice once one is made.
    const [pickedPeriodId, setPickedPeriodId] = useState<string | null>(null);
    const { data: currentUser, isLoading: isReaderLoading } = useGetUserById({ userId: 'me' });
    // Nobody reads their own evaluation, their own growth or the notes written about them, whatever
    // their role: these are the manager's screens, and the employee is given the same facts in their
    // portal. One condition for the three, so the profile answers the question in a single place.
    const isOwnProfile = currentUser !== undefined && currentUser.id === id;
    const readsSomeoneElse = currentUser !== undefined && !isOwnProfile;
    // The server refuses the evaluation to the employee it is about, so it is asked for only once the
    // reader is known to be someone else: asked on one's own profile, the refusal would take the
    // whole page down with it.
    const {
        score: scoreData,
        isScoreLoading,
        scoreError,
        isScoreStale,
        skillsetFilter,
        onSkillsetFilterChange,
        scorePeriodLabel,
    } = useProfileScore(readsSomeoneElse ? id : undefined);
    // A reader of their own objectives alone has no competencies to ask the server for.
    const { data: assignmentData } = useGetEmployeeCompetencyAssignment(
        ownView === 'objectives' ? undefined : id,
    );
    const {
        data: employeeUser,
        error: employeeError,
        isLoading: isEmployeeLoading,
    } = useGetUserById({ userId: id ?? '' });
    const { rosterEmployee, copyCandidates } = useRosterEmployee({
        employeeId: id,
        employee: employeeUser,
        assignment: assignmentData,
        enabled: !ownObjectivesOnly,
    });
    const { data: permissions } = useGetUserPermissions();
    // Whether the reader is an administrator is a fact about the session, not about the directory
    // entry of the person being looked up.
    const { isAdminUser } = useAtomValue(userInstanceAtom);
    const showsObjectives = true;
    const { getJobTitle } = useCompanyInformation();
    const employeeJobTitle = employeeUser?.jobTitleId ? getJobTitle(employeeUser.jobTitleId) : '';

    const canStartEvaluations = useMemo(() => {
        if (!permissions) return false;

        const hasScope =
            isAdminUser ||
            permissions.canManageTeamPerformance ||
            permissions.canManageAllDepartmentsPerformance;

        return hasScope && permissions.canEditEvaluations;
    }, [permissions, isAdminUser]);

    // Enrich scoreData with employeeName and avatarUrl for initials fallback
    const enrichedScoreData = useMemo(() => {
        if (!scoreData) return null;
        return {
            ...scoreData,
            employeeName: employeeUser
                ? `${employeeUser.firstName} ${employeeUser.lastName}`
                : undefined,
            avatarUrl:
                scoreData.avatarUrl || getProfilePictureUrl(employeeUser?.profilePictureUrl ?? ''),
        };
    }, [scoreData, employeeUser]);

    const goToCreateProfile = (employeeId: string, name: string) => {
        const wizardState = buildCreateProfileWizardLocationState({
            returnMode: embedded
                ? CreateProfileReturnMode.ColleaguePerformanceProfile
                : CreateProfileReturnMode.PerformanceProfile,
            returnEmployeeId: employeeId,
            returnEmployeeName: name,
        });
        const search = buildCreateProfileWizardSearch(wizardState);
        if (embedded) {
            history.push({
                pathname: RouteName.CompetencyProfileCreate,
                search,
                state: wizardState,
            });

            return;
        }

        navigate({
            path: `${RouteName.CompetencyProfileCreate}${search}`,
            state: wizardState,
        });
    };

    const objectivesTab = (
        <ObjectivesTab
            employeeId={id ?? ''}
            pickedPeriodId={pickedPeriodId}
            onPickPeriod={setPickedPeriodId}
        />
    );

    if (ownObjectivesOnly) {
        return (
            <div className={'p-6'}>
                <ProfileTabs
                    employee={employeeUser}
                    jobTitle={employeeJobTitle}
                    visibleTabs={
                        ownView === 'objectivesAndCompetencies'
                            ? OWN_OBJECTIVES_AND_COMPETENCIES_TABS
                            : OWN_OBJECTIVES_TABS
                    }
                    canReadNotes={false}
                >
                    {(activeTab) =>
                        activeTab === 'competencies' ? (
                            // Read-only on one's own profile: no assignment flow behind it, so no
                            // permission to hand down and nothing to offer.
                            <CompetencyProfileCard employeeId={id ?? ''} />
                        ) : (
                            objectivesTab
                        )
                    }
                </ProfileTabs>
            </div>
        );
    }

    // Who the reader is belongs to the score's answer: rendered before the directory names them, the
    // Évaluation tab would ask the server for the very results it is about to hide.
    if (isScoreLoading || isReaderLoading || isEmployeeLoading) {
        return <ProfileStatus text={t('performanceProfile.loading')} />;
    }

    if (isRefusedError(employeeError)) {
        return <ProfileStatus text={t('performanceProfile.notFound')} />;
    }

    // Whose profile this is, is the page's fact: decided here, no tab asks the server a question whose
    // refusal it would have to interpret, and the tabs never wait on a score nobody may read.
    if (isOwnProfile) {
        return (
            <div className={'p-6'}>
                <ProfileTabs
                    employee={employeeUser}
                    jobTitle={employeeJobTitle}
                    canReadNotes={false}
                    showsObjectives={showsObjectives}
                >
                    {(activeTab) => {
                        switch (activeTab) {
                            case 'assessment':
                                return (
                                    <AssessmentForbiddenNotice
                                        employeeId={id ?? ''}
                                        canStartEvaluations={canStartEvaluations}
                                    />
                                );
                            case 'growth':
                                return <GrowthForbiddenNotice reason={'ownProfile'} />;
                            case 'objectives':
                                return objectivesTab;
                            default:
                                return null;
                        }
                    }}
                </ProfileTabs>
            </div>
        );
    }

    if (scoreError) {
        return (
            <ProfileStatus
                text={t('performanceProfile.error')}
                isError
            />
        );
    }

    if (!enrichedScoreData) {
        return <ProfileStatus text={t('performanceProfile.noData')} />;
    }

    if (viewMode !== 'default' && rosterEmployee) {
        return (
            <ProfileAssignmentScreen
                screen={viewMode}
                employee={rosterEmployee}
                copyCandidates={copyCandidates}
                canManageCompetencies={permissions?.canManageCompetencies ?? false}
                onCreateGuided={goToCreateProfile}
                onClose={closeAssignment}
            />
        );
    }

    return (
        <div className={'p-6'}>
            <ProfileTabs
                employee={employeeUser}
                jobTitle={employeeJobTitle}
                canReadNotes={readsSomeoneElse}
                showsObjectives={showsObjectives}
                initialTab={openingTab}
            >
                {(activeTab, goToTab) => {
                    switch (activeTab) {
                        case 'profil':
                            return <PlaceholderTab />;
                        case 'summary':
                            return <PlaceholderTab />;
                        case 'assessment':
                            return (
                                <AssessmentTab
                                    scoreData={enrichedScoreData}
                                    employeeId={id ?? ''}
                                    onEdit={() => openAssignment('assessment')}
                                    canStartEvaluations={canStartEvaluations}
                                    canAssignCompetencyProfiles={
                                        permissions?.canAssignCompetencyProfiles ?? false
                                    }
                                    pickedPeriodId={pickedPeriodId}
                                    onPickPeriod={setPickedPeriodId}
                                    onViewAllObjectives={() => goToTab('objectives')}
                                    showsObjectives={showsObjectives}
                                    skillsetFilter={skillsetFilter}
                                    onSkillsetFilterChange={onSkillsetFilterChange}
                                    scorePeriodLabel={scorePeriodLabel}
                                    isScoreStale={isScoreStale}
                                />
                            );
                        case 'growth':
                            return (
                                <GrowthTab
                                    employeeId={id ?? ''}
                                    employee={employeeUser}
                                    scoreData={enrichedScoreData}
                                    canReadNotes={readsSomeoneElse}
                                    onEditCurrentProfile={() => openAssignment('growth')}
                                    onEditTargetProfile={() =>
                                        openAssignment('growth', 'assignTarget')
                                    }
                                    scorePeriodLabel={scorePeriodLabel}
                                />
                            );
                        case 'objectives':
                            return objectivesTab;
                        case 'notes':
                            return (
                                readsSomeoneElse && (
                                    <NotesTab
                                        employeeId={id ?? ''}
                                        employee={employeeUser}
                                    />
                                )
                            );
                        default:
                            return null;
                    }
                }}
            </ProfileTabs>
        </div>
    );
};
